import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { NotificationTemplate } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { EmailSenderSection } from '@/features/stores';
import { useStore } from '@/shared/store/store-context';

import { TemplateEditorDialog } from '../components/template-editor-dialog';
import { TemplatesList } from '../components/templates-list';
import { useToggleTemplate } from '../hooks/use-notification-mutations';
import { useNotificationTemplates } from '../hooks/use-notification-templates';

function NotificationTemplates() {
  const { t } = useTranslation('notifications');
  const store = useStore();
  const { can } = usePermissions();
  const message = useErrorMessage();
  const query = useNotificationTemplates(store.id);
  const toggle = useToggleTemplate(store.id);
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const canWrite = can('notifications.write');

  const onToggle = (template: NotificationTemplate) =>
    toggle.mutate(template, {
      onSuccess: (saved) =>
        toast.success(
          t(saved.isEnabled ? 'toasts.enabled' : 'toasts.disabled', {
            title: t(`event.${saved.event}`),
          }),
        ),
      onError: (error) => toast.error(message(error)),
    });

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/org/stores/$storeId', params: { storeId: store.id } }}
        backLabel={t('back')}
      />
      <Alert className="mb-4">
        <Mail aria-hidden />
        <AlertDescription>{t('intro')}</AlertDescription>
      </Alert>
      <Can permission="stores.read">
        <div className="mb-6">
          <EmailSenderSection storeId={store.id} />
        </div>
      </Can>
      {query.isPending ? (
        <Skeleton className="h-64" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <TemplatesList
          templates={query.data}
          canWrite={canWrite}
          busyEvent={toggle.isPending ? toggle.variables.event : null}
          onToggle={onToggle}
          onEdit={setEditing}
        />
      )}
      {editing ? (
        <TemplateEditorDialog
          key={editing.event}
          storeId={store.id}
          template={editing}
          readOnly={!canWrite}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </PageContent>
  );
}

export function NotificationTemplatesPage() {
  return (
    <FeatureErrorBoundary name="notifications">
      <NotificationTemplates />
    </FeatureErrorBoundary>
  );
}
